import {
  resolveContactFields,
  type ContactDataForFields,
} from "~/convex/jobs/precedents/fieldMapping";
import {
  buildContactFieldKey,
  type ContactFieldPart,
} from "~/convex/jobs/precedents/contactFields";
import { deriveNameParts, composeFullName } from "~/convex/contacts/nameParts";

/** A fan-out contact, narrowed to what completeness logic needs. */
export type ContactForCompleteness = {
  _id: string;
  displayName: string;
  data: ContactDataForFields;
};

/**
 * Current value of a contact-field part, as a plain string ("" when empty).
 * Single source of truth for both the fan-out preview and the generate-time
 * check — mirrors the silent-drop the generator applies in
 * `resolveContactFields`.
 */
export function getContactPartValue(
  part: ContactFieldPart,
  data: ContactDataForFields,
): string {
  const first = (data.addresses ?? [])[0];
  switch (part) {
    case "name":
      return data.displayName ?? "";
    // Person-name parts (MOR-643). Mirror `resolveContactFields`: only person
    // contacts expose first/middle/last/full; legacy rows derive from the name.
    case "first_name":
      return data.contactType === "person"
        ? deriveNameParts(data).firstName
        : "";
    case "middle_name":
      return data.contactType === "person"
        ? deriveNameParts(data).middleName
        : "";
    case "last_name":
      return data.contactType === "person"
        ? deriveNameParts(data).lastName
        : "";
    case "full_name": {
      if (data.contactType !== "person") return "";
      const p = deriveNameParts(data);
      return composeFullName(p.firstName, p.middleName, p.lastName);
    }
    case "date_of_birth":
      return data.dateOfBirth ?? "";
    case "email":
      return data.email ?? "";
    case "phone":
      return data.phone ?? "";
    case "abn":
      return data.abn ?? "";
    case "acn":
      return data.acn ?? "";
    case "address_line1":
      return first?.line1 ?? "";
    case "address_line2":
      return first?.line2 ?? "";
    case "address_suburb":
      return first?.suburb ?? "";
    case "address_state":
      return first?.state ?? "";
    case "address_postcode":
      return first?.postcode ?? "";
    case "address_full": {
      const a = (data.addresses ?? []).find((addr) =>
        Boolean(addr.line1 || addr.suburb || addr.state || addr.postcode),
      );
      if (!a) return "";
      return [a.line1, a.line2, a.suburb, a.state, a.postcode]
        .filter(Boolean)
        .join(", ");
    }
    default:
      // Derived / overlay parts (external administration, primary contact
      // person, name-with-status) have no single backing field — read them
      // from the same resolver the generator uses so the preview and the
      // generate-time check can never disagree with the finished document.
      // These parts are job-domain only (see CONTACT_FIELD_PARTS).
      return (
        resolveContactFields(data, "job")[
          buildContactFieldKey({ domain: "job", part })
        ] ?? ""
      );
  }
}

/** Empty (contact, part) pairs among the parts a template references. */
export function missingContactFields(
  contacts: ContactForCompleteness[],
  expectedParts: ContactFieldPart[],
): Array<{ contactId: string; displayName: string; part: ContactFieldPart }> {
  const out: Array<{
    contactId: string;
    displayName: string;
    part: ContactFieldPart;
  }> = [];
  for (const contact of contacts) {
    for (const part of expectedParts) {
      if (getContactPartValue(part, contact.data).trim() === "") {
        out.push({
          contactId: contact._id,
          displayName: contact.displayName,
          part,
        });
      }
    }
  }
  return out;
}
